import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Button, DrawerHeader, DrawerBody, DrawerFooter, TextField, Drawer
} from '@one-thd/sui-atomic-components';
import {
  QueryProvider, extend, useDataModel,
  arrayOf,
  number as numberType,
  string as stringType,
  customType,
  params, hoist
} from '@thd-nucleus/data-sources';
import { ProductListContext } from '../ProductListContext';

export const CreateGroup = ({ storeId, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { list, onEditSuccess } = useContext(ProductListContext);

  const [listOperations] = useDataModel('listOperations', {
    variables: {
      id: list?.id
    },
    fetchPolicy: 'no-cache',
    context: { withAuth: true },
    lazy: true
  });

  const handleClose = () => {
    setIsOpen(false);
    setGroupName('');
  };

  const handleOpen = () => {
    setIsOpen(true);
    // Analytics: Create group opened
    window.LIFE_CYCLE_EVENT_BUS.trigger('product-list.create-group');
  };

  const handleSubmit = async (event) => {
    if (event) {
      event.preventDefault();
    }

    if (!groupName.trim() || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Get the next group order (highest existing order + 1)
      const existingGroups = list?.groups || [];
      const nextGroupOrder = existingGroups.length > 0
        ? Math.max(...existingGroups.map((group) => parseInt(group.groupOrder || 0, 10))) + 1
        : 1;

      await listOperations({
        variables: {
          operationName: 'Create',
          id: list.id,
          groups: [{
            groupName: groupName.trim(),
            groupOrder: nextGroupOrder.toString(),
            products: []
          }]
        }
      });

      // Analytics: Group successfully created
      window.LIFE_CYCLE_EVENT_BUS.trigger('product-list.create-group-success');

      onEditSuccess({});
      handleClose();
    } catch (error) {
      // Error creating group - could show user feedback here
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmit(event);
    }
  };

  return (
    <>
      <Button
        data-testid="create-group-drawer-button"
        onClick={handleOpen}
        variant="secondary"
        type="button"
        disabled={disabled}
      >
        Create Group
      </Button>
      <Drawer
        open={isOpen}
        onClose={handleClose}
      >
        <DrawerHeader title="Create Group" onClose={handleClose} />

        <DrawerBody>
          <form
            className="sui-grid sui-gap-6"
            onSubmit={handleSubmit}
          >
            <TextField
              id="group-name"
              label="Group Name"
              value={groupName}
              onChange={(event) => setGroupName(event.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter group name"
              type="text"
              fullWidth
              required
              autoFocus
              InputProps={{
                inputProps: {
                  'data-testid': 'group-name-input',
                }
              }}
            />
          </form>
        </DrawerBody>

        <DrawerFooter position="center">
          <div className="sui-flex sui-flex-col sui-gap-2">
            <Button
              data-testid="create-group-submit-button"
              variant="primary"
              fullWidth
              onClick={handleSubmit}
              disabled={!groupName.trim() || isSubmitting}
              type="submit"
            >
              {isSubmitting ? 'Creating...' : 'Create Group'}
            </Button>
            <Button
              data-testid="create-group-cancel-button"
              variant="secondary"
              fullWidth
              onClick={handleClose}
              disabled={isSubmitting}
              type="button"
            >
              Cancel
            </Button>
          </div>
        </DrawerFooter>
      </Drawer>
    </>
  );
};

CreateGroup.dataModel = extend(
  {
    listOperations: params({
      operationName: customType('Operation').enum(['Create'], 'Create'),
      id: stringType().isRequired(),
      groups: arrayOf(customType('GroupInput').shape({
        groupName: stringType(),
        groupOrder: stringType(),
        products: arrayOf(customType('ProductInput').shape({
          itemId: stringType(),
          quantity: numberType(),
          itemOrder: numberType(),
          comments: stringType(),
          intent: stringType() // NEW
        }))
      })),
      name: stringType(),
      listAccessType: customType('AccessTypes').enum(['Shared', 'Private'])
    }).mutation().shape({
      id: stringType(),
      groups: arrayOf(customType('Group').shape({
        groupId: stringType(),
        groupName: stringType(),
        groupOrder: stringType(),
        status: stringType(),
        items: arrayOf(customType('Item').shape({
          itemId: stringType(),
          quantity: numberType(),
          itemOrder: numberType(),
          comments: stringType(),
          status: stringType(),
          intent: stringType(),
          intentId: stringType()
        }))
      }))
    })
  }
);

CreateGroup.propTypes = {
  storeId: PropTypes.string.isRequired,
  disabled: PropTypes.bool
};

CreateGroup.defaultProps = {
  disabled: false
};
